// Supplier switch for the results topbar.
//
// It replaces the read-only `.tag.source` badge that used to sit here, because
// the two jobs are the same job: the control that tells you *which* supplier
// answered is the natural place to ask for a different one. A segmented pill
// rather than the search card's dropdown — with two or three suppliers, one
// click beats open-then-pick, and every option stays legible without opening
// anything.
//
// Switching re-runs the SAME search against the other supplier. Prices, room
// types and availability are all supplier-specific, so this is a fresh search
// and not a filter — the caller reloads the page with the new results, which is
// why the pressed segment shows a spinner rather than an optimistic selection.

interface Props<T extends string> {
  sources: ReadonlyArray<{ value: T; label: string }>;
  /** The supplier that served the results currently on screen. */
  value: T;
  onChange: (source: T) => void;
  /** The supplier being fetched right now, if any. */
  pending?: T | null;
  disabled?: boolean;
}

export function ProviderSwitch<T extends string>(props: Props<T>) {
  const busy = props.pending != null;

  // One supplier is not a choice — render the plain badge the topbar used to
  // show rather than a segmented control with nothing to segment.
  if (props.sources.length < 2) {
    const only = props.sources.find((s) => s.value === props.value);
    return only ? <span className="tag source">{only.label}</span> : null;
  }

  return (
    <div className="provider-switch" role="group" aria-label="Search supplier">
      <span className="provider-switch-label">Source</span>
      {props.sources.map((source) => {
        const active = source.value === props.value;
        const loading = props.pending === source.value;
        return (
          <button
            key={source.value}
            type="button"
            className={`provider-option${active ? " active" : ""}${loading ? " loading" : ""}`}
            // The active supplier is already on screen; re-running its own
            // search here would be indistinguishable from a no-op that takes
            // ten seconds.
            disabled={active || busy || props.disabled}
            aria-pressed={active}
            title={active ? `Showing ${source.label} results` : `Search ${source.label} instead`}
            onClick={() => props.onChange(source.value)}
          >
            {loading && <span className="provider-spinner" aria-hidden="true" />}
            {source.label}
          </button>
        );
      })}
    </div>
  );
}
